<template>
  <div class="rmr-page-shell">
    <ctc-assistant-hero
      :active-view="page.activeView"
      :pending-count="workspace.state.pendingVerifications.length"
      @update:activeView="setActiveView"
    />

    <section class="rmr-page-shell__content">
      <rmr-notice
        v-if="workspace.state.loadError"
        variant="error"
        title="Assistant CTC indisponible"
        :text="workspace.state.loadError"
      />

      <template v-else>
        <ctc-create-view
          v-if="page.activeView === views.create"
          :flow="workspace.createFlow"
          :sites="workspace.state.sites"
          :pending-verifications="workspace.state.pendingVerifications"
          :abandoning-verification-ids="
            workspace.state.abandoningVerificationIds
          "
          @launch="launch"
          @reset="workspace.resetCreateFlow"
          @resume-verification="workspace.resumeVerification"
          @leave-verification="workspace.leaveVerification"
          @abandon-verification="workspace.abandonVerification"
        />
        <ctc-articles-view
          v-else-if="page.activeView === views.articles"
          :articles="workspace.state.articles"
          :loading="
            workspace.state.initializing || workspace.state.articlesLoading
          "
          :total="workspace.state.articlesTotal"
          :page="workspace.state.articlesPage"
          :limit="workspace.state.articlesLimit"
          :highlighted-id="workspace.state.highlightedArticleId"
          :retrying-ids="workspace.state.retryingIds"
          :rescheduling-ids="workspace.state.reschedulingIds"
          @retry-affilizz="retryArticle"
          @reschedule="openReschedule"
          @load="workspace.loadArticles"
        />
        <ctc-opportunities-view
          v-else-if="page.activeView === views.opportunities"
          :opportunities="workspace.state.opportunities"
          :loading="
            workspace.state.initializing || workspace.state.opportunitiesLoading
          "
          :total="workspace.state.opportunitiesTotal"
          :page="workspace.state.opportunitiesPage"
          :limit="workspace.state.opportunitiesLimit"
          :active-opportunity-id="workspace.createFlow.state.opportunityId"
          :start-blocked-reason="opportunityStartBlockedReason"
          @create-article="createFromOpportunity"
          @load="workspace.loadOpportunities"
        />
        <ctc-configuration-view
          v-else
          :sites="workspace.state.siteSettings"
          :loading="workspace.state.siteSettingsLoading"
          :total="workspace.state.siteSettingsTotal"
          :page="workspace.state.siteSettingsPage"
          :limit="workspace.state.siteSettingsLimit"
          :saving-ids="workspace.state.savingSiteSettingIds"
          @load="workspace.loadSiteSettings"
          @save="saveSiteSetting"
        />
      </template>
    </section>

    <rmr-confirm-dialog
      v-model="confirm.open"
      title="Remplacer le parcours en cours ?"
      :message="replaceMessage"
      confirm-label="Remplacer"
      @confirm="confirmReplace"
    />

    <rmr-confirm-dialog
      v-model="publishConfirmOpen"
      title="Demander une publication sans relecture ?"
      message="Ce statut sera appliqué lors de la génération de l’article : il sera mis en ligne dans le CMS sans relecture. Choisissez « Brouillon » pour le relire d’abord."
      confirm-label="Confirmer la demande"
      @confirm="sendArticle"
    />

    <ctc-reschedule-modal
      v-model="reschedule.open"
      :article="rescheduledArticle"
      :saving="
        !!reschedule.articleId &&
        (workspace.state.reschedulingIds.includes(reschedule.articleId) ||
          workspace.state.retryingIds.includes(reschedule.articleId))
      "
      @save="saveReschedule"
    />

    <v-snackbar
      v-model="launchNotice.show"
      :color="launchNotice.color"
      timeout="6000"
    >
      <strong>{{ launchNotice.message }}</strong>
    </v-snackbar>

    <v-snackbar
      :value="!!workspace.state.actionError"
      color="error"
      timeout="6000"
      @input="(open) => !open && workspace.dismissError()"
    >
      {{ workspace.state.actionError }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
} from "@vue/composition-api";

import CtcArticlesView from "@/components/ai-factory/ctc/articles/CtcArticlesView.vue";
import CtcRescheduleModal from "@/components/ai-factory/ctc/articles/CtcRescheduleModal.vue";
import CtcAssistantHero from "@/components/ai-factory/ctc/CtcAssistantHero.vue";
import CtcConfigurationView from "@/components/ai-factory/ctc/configuration/CtcConfigurationView.vue";
import CtcCreateView from "@/components/ai-factory/ctc/create/CtcCreateView.vue";
import CtcOpportunitiesView from "@/components/ai-factory/ctc/opportunities/CtcOpportunitiesView.vue";
import {
  VIEWS,
  VIEW_ARTICLES,
  VIEW_CONFIGURATION,
  VIEW_CREATE,
  VIEW_OPPORTUNITIES,
  VIEW_QUERY_PARAM,
} from "@/components/ai-factory/ctc/constants";
import { launchNoticeOf } from "@/components/ai-factory/ctc/ctc-model";
import { createCtcService } from "@/components/ai-factory/ctc/ctcService";
import { useCtcWorkspace } from "@/components/ai-factory/ctc/useCtcWorkspace";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";

import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";
import useAxios from "@/hooks/useAxios";
import { saveRmraView } from "@/utils/rmr-analytics";

export default {
  name: "CtcArticleAssistant",
  components: {
    CtcArticlesView,
    CtcAssistantHero,
    CtcConfigurationView,
    CtcCreateView,
    CtcOpportunitiesView,
    CtcRescheduleModal,
    RmrConfirmDialog,
    RmrNotice,
  },
  setup() {
    const { route } = useProxy();
    const { addQueryStringParam, getUrlValue } = useQueryString();
    const { axiosGet, axiosPatch, axiosPost } = useAxios();

    // Sites, product analysis, RMR drafts, Affilizz contents and opportunities
    // are real. The backend hands ready drafts to AifPostGeneration.
    const workspace = useCtcWorkspace(
      createCtcService({ axiosGet, axiosPatch, axiosPost })
    );

    const page = reactive({
      activeView: VIEW_CREATE,
      isInitialized: false,
    });
    const confirm = reactive({ open: false, opportunityId: null });
    const publishConfirmOpen = ref(false);
    const reschedule = reactive({ open: false, articleId: null });
    const launchNotice = reactive({
      show: false,
      message: "",
      color: "success",
    });

    // =>  METHODS
    const setActiveView = (view) => {
      page.activeView = VIEWS.includes(view) ? view : VIEW_CREATE;

      // Only user changes are written to the URL, never the initial restore.
      if (page.isInitialized) {
        addQueryStringParam({
          param: VIEW_QUERY_PARAM,
          value: page.activeView,
        });
      }
      if (page.activeView === VIEW_CONFIGURATION) {
        workspace.loadSiteSettings();
      }
    };

    const sendArticle = async () => {
      const article = await workspace.launchArticle();
      if (!article) return;
      Object.assign(launchNotice, launchNoticeOf(article), { show: true });
      setActiveView(VIEW_ARTICLES);
    };

    const launch = () => {
      if (workspace.createFlow.needsPublishConfirmation.value) {
        publishConfirmOpen.value = true;
        return;
      }
      sendArticle();
    };

    const createFromOpportunity = async (opportunityId) => {
      const result = await workspace.requestOpportunity(opportunityId);
      if (result === "confirm") {
        Object.assign(confirm, { open: true, opportunityId });
      } else if (result === "started" || result === "current") {
        setActiveView(VIEW_CREATE);
      }
    };

    const confirmReplace = async () => {
      if (await workspace.startFromOpportunity(confirm.opportunityId)) {
        setActiveView(VIEW_CREATE);
      }
      confirm.opportunityId = null;
    };

    const saveSiteSetting = async (payload, done) => {
      const success = await workspace.updateSiteSetting(payload);
      if (typeof done === "function") done(success);
    };

    const showNotice = (article) => {
      if (article) {
        Object.assign(launchNotice, launchNoticeOf(article), { show: true });
      }
    };

    const retryArticle = async (articleId) => {
      showNotice(await workspace.retryAffilizz(articleId));
    };

    const openReschedule = (articleId) => {
      Object.assign(reschedule, { open: true, articleId });
    };

    const saveReschedule = async ({ id, postStatus, scheduledAt }) => {
      const article = await workspace.rescheduleArticle(id, {
        postStatus,
        scheduledAt,
      });
      if (!article) return;
      Object.assign(reschedule, { open: false, articleId: null });
      showNotice(article);
    };

    // =>  HOOKS
    onMounted(() => {
      const urlView = getUrlValue({ param: VIEW_QUERY_PARAM });
      page.activeView = VIEWS.includes(urlView) ? urlView : VIEW_CREATE;
      saveRmraView(route);
      page.isInitialized = true;
      workspace.init();
      if (page.activeView === VIEW_CONFIGURATION) {
        workspace.loadSiteSettings();
      }
    });

    // Stops the highlight timer and ignores answers still in flight.
    onBeforeUnmount(() => workspace.dispose());

    // =>  DATA
    return {
      // Once verified, the product has a pending draft and Affilizz contents.
      replaceMessage: computed(() =>
        workspace.createFlow.state.verification.article
          ? "Le produit en cours de préparation sera abandonné : sa vérification Affilizz est annulée et son opportunité libérée. Les contenus déjà créés chez Affilizz y restent, sans être utilisés."
          : "Le produit en cours de préparation sera abandonné. Rien n’a encore été créé pour lui dans RMR ni dans Affilizz."
      ),
      confirm,
      confirmReplace,
      openReschedule,
      reschedule,
      rescheduledArticle: computed(
        () =>
          workspace.state.articles.find(
            (article) => article.id === reschedule.articleId
          ) || null
      ),
      retryArticle,
      saveReschedule,
      createFromOpportunity,
      launch,
      launchNotice,
      publishConfirmOpen,
      saveSiteSetting,
      sendArticle,
      opportunityStartBlockedReason: computed(
        () => workspace.opportunityStartBlockedReason.value
      ),
      page,
      setActiveView,
      views: {
        articles: VIEW_ARTICLES,
        configuration: VIEW_CONFIGURATION,
        create: VIEW_CREATE,
        opportunities: VIEW_OPPORTUNITIES,
      },
      workspace,
    };
  },
};
</script>
